import React, { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";

const COLORS = ["#ff4f9a", "#4f6aff", "#8884d8"];

/**
 * Normaliza o gênero para evitar cair tudo em "Outros"
 * Ajuste as variações conforme seu app mobile grava no Firestore.
 */
function normalizeGender(raw) {
  const g = String(raw || "").toLowerCase().trim();

  // feminino
  if (
    g === "feminino" ||
    g === "mulher" ||
    g === "female" ||
    g === "f" ||
    g.includes("fem")
  ) {
    return "feminino";
  }

  // masculino
  if (
    g === "masculino" ||
    g === "homem" ||
    g === "male" ||
    g === "m" ||
    g.includes("masc")
  ) {
    return "masculino";
  }

  // vazio ou qualquer outro valor => outros
  if (!g) return "outros";
  return "outros";
}

function GenderPieChart() {
  const [counts, setCounts] = useState({ female: 0, male: 0, other: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ✅ Troca getDocs por onSnapshot: atualiza sozinho quando mudar a base
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        let female = 0;
        let male = 0;
        let other = 0;

        snap.forEach((doc) => {
          const u = doc.data();
          const g = normalizeGender(u.gender);

          if (g === "feminino") female++;
          else if (g === "masculino") male++;
          else other++;
        });

        setCounts({ female, male, other });
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar gênero:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const total = counts.female + counts.male + counts.other;

  // ✅ Guardamos CONTAGEM no value (mais correto pro gráfico)
  const chartData = useMemo(() => {
    if (!total) return [];
    return [
      { name: "Feminino", value: counts.female },
      { name: "Masculino", value: counts.male },
      { name: "Outros", value: counts.other },
    ];
  }, [counts, total]);

  // ✅ Função pra calcular % (sem quebrar soma por arredondamento)
  const pct = (v) => {
    if (!total) return 0;
    return Math.round((v / total) * 100);
  };

  if (loading) {
    return (
      <div className="chartBox">
        <h3>Distribuição por gênero ⚧</h3>
        <div className="chart-loading">Carregando gráfico...</div>
      </div>
    );
  }

  return (
    <div className="chartBox">
      <h3>Distribuição por gênero ⚧</h3>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              outerRadius="80%"
              dataKey="value"
              // ✅ Label mostra %, mas o gráfico usa contagem real
              label={(entry) => `${entry.name}: ${pct(entry.value)}%`}
            >
              {chartData.map((entry, index) => (
                <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>

            <Tooltip
              // ✅ Tooltip mostra contagem + %
              formatter={(value, name) => {
                const valueNum = Number(value) || 0;
                return [`${valueNum} (${pct(valueNum)}%)`, String(name)];
              }}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ff4f9a",
                borderRadius: "8px",
                fontSize: "12px",
                padding: "8px",
              }}
            />

            <Legend
              // ✅ Legend mostra nome e (opcional) contagem total
              formatter={(value) => String(value)}
              wrapperStyle={{
                fontSize: "12px",
                paddingTop: "10px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* ✅ Rodapé pra validar rapidamente se “bate” com a base */}
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: "#666",
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span>
          Total: <strong>{total}</strong>
        </span>
        <span>
          Feminino: <strong>{counts.female}</strong>
        </span>
        <span>
          Masculino: <strong>{counts.male}</strong>
        </span>
        <span>
          Outros: <strong>{counts.other}</strong>
        </span>
      </div>
    </div>
  );
}

export default GenderPieChart;
